import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Play } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import XeroMetricInfo, {
  type MetricDefinition,
} from "@/components/admin/XeroMetricInfo";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { formatWhen } from "@/lib/xeroRunFormat";
import {
  XERO_PIPELINE_RUNS_QUERY_KEY,
  readPipelineBusy,
  triggerXeroPipeline,
  type XeroConnection,
  type XeroPipelineActiveRun,
  type XeroPipelineBusy,
  type XeroPipelineMode,
  type XeroPipelineRun,
} from "@/services/xeroApi";

const MODE_PHRASE: Record<XeroPipelineMode, string> = {
  incremental: "An incremental load",
  backfill: "A backfill",
};

function describeRunning(running: XeroPipelineActiveRun): string {
  const when = running.started_at ?? running.triggered_at;
  const at = when ? ` at ${formatWhen(when)}` : "";
  if (running.source === "fabric") {
    const how = running.invoke_type === "Scheduled" ? "by the Fabric schedule" : "in Fabric";
    return `A run started ${how}${at} is still going.`;
  }
  const what = running.mode ? MODE_PHRASE[running.mode] : "A run";
  if (running.status === "waiting") {
    const who = running.started_by_you ? "you queued" : "another admin queued";
    return `${what} ${who} is next in line and starts first.`;
  }
  const who = running.started_by_you ? "you started" : "another admin started";
  return `${what} ${who}${at} is still going.`;
}

const MODES: { value: XeroPipelineMode; label: string; description: string }[] = [
  {
    value: "incremental",
    label: "Incremental",
    description:
      "Resume each organisation from its watermark. Only journals created since the last successful load are downloaded.",
  },
  {
    value: "backfill",
    label: "Backfill",
    description:
      "Reload each organisation's history from its retrieval date, or from the beginning if none is set. Safe to repeat, but it spends Xero call budget in proportion to the history.",
  },
];

const METRICS: MetricDefinition[] = [
  {
    term: "Incremental",
    definition:
      "Resumes each organisation above its stored watermark — the highest Xero journal number already loaded. Xero journals are immutable once posted, so anything above that number is new. This is the mode the daily schedule uses.",
  },
  {
    term: "Backfill",
    definition:
      "Starts each organisation from its retrieval date, or from zero if none is set, and re-pulls history from there. Safe to repeat, because rows are merged on journal line rather than appended, and unchanged rows are not rewritten. It does spend Xero call budget in proportion to the history, and Xero allows only 5,000 calls per organisation per rolling 24 hours.",
  },
  {
    term: "Organisations",
    definition:
      "Every organisation the current Xero consent grants. Leaving all of them ticked sends no filter at all, so the run picks up any organisation Xero grants between now and when it starts.",
  },
  {
    term: "Retrieval date",
    definition:
      "The date Prism should retrieve that organisation's journals from, recorded in Prism rather than in Xero. Published to Fabric's org_rules table, which the notebooks read as a floor on journal date: journals dated before it are left out when the journals are consolidated. An organisation with no retrieval date set is kept in full.",
  },
];

interface Props {
  connections: XeroConnection[];s
  disabledReason: string | null;
  onTriggered: (run: XeroPipelineRun) => void;
  queueOnly?: boolean;
}

export default function XeroPipelineTriggerCard({
  connections,
  disabledReason,
  onTriggered,
  queueOnly = false,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<XeroPipelineMode>("incremental");

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(connections.map((c) => c.tenant_id)),
  );
  const [confirming, setConfirming] = useState(false);

  const [refusal, setRefusal] = useState<XeroPipelineBusy | null>(null);

  const allSelected = selected.size === connections.length && connections.length > 0;
  const noneSelected = selected.size === 0;

  const tenantIds = useMemo(
    () => (allSelected ? [] : connections.filter((c) => selected.has(c.tenant_id)).map((c) => c.tenant_id)),
    [allSelected, connections, selected],
  );

  const trigger = useMutation({
    mutationFn: (queueIfBusy: boolean) =>
      triggerXeroPipeline({ mode, tenant_ids: tenantIds, queue_if_busy: queueIfBusy }),
    onSuccess: (run) => {
      setRefusal(null);
      toast(
        run.status === "waiting"
          ? {
              title: "Run queued",
              description: "It starts automatically as soon as the run in progress finishes.",
            }
          : {
              title: mode === "backfill" ? "Backfill started" : "Incremental load started",
              description: `Run ${run.run_id} has been handed to Fabric.`,
            },
      );
      queryClient.invalidateQueries({ queryKey: XERO_PIPELINE_RUNS_QUERY_KEY });
      onTriggered(run);
    },
    onError: (e) => {
      const details = readPipelineBusy(e);
      if (details) {
        setRefusal(details);
        return;
      }
      toast({
        variant: "destructive",
        title: "Couldn’t start the pipeline",
        description: (e as Error).message,
      });
    },
    onSettled: () => setConfirming(false),
  });

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(connections.map((c) => c.tenant_id)) : new Set());
  };

  const toggleOne = (tenantId: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(tenantId);
      else next.delete(tenantId);
      return next;
    });
  };

  const start = () => {
    if (mode === "backfill") {
      setConfirming(true);
      return;
    }
    trigger.mutate(queueOnly);
  };

  const blocked = disabledReason ?? (noneSelected ? "Select at least one organisation." : null);
  const busy = trigger.isPending;

  return (
    <section className="space-y-5 rounded-lg border border-border p-5">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-medium text-foreground">
            {queueOnly ? "Queue the next run" : "Run the pipeline"}
          </h3>
          <XeroMetricInfo title="Run the pipeline" metrics={METRICS} />
        </div>
        <p className="text-xs text-muted-foreground">
          {queueOnly
            ? "Waits in Prism and starts automatically as soon as the run in progress finishes. Only one run can wait at a time."
            : "Starts the Fabric pipeline directly. This view becomes the live progress while a load is running, and comes back when it finishes."}
        </p>
      </div>

      <RadioGroup
        value={mode}
        onValueChange={(value) => setMode(value as XeroPipelineMode)}
        className="grid gap-3 sm:grid-cols-2"
        aria-label="Load mode"
      >
        {MODES.map((option) => (
          <label
            key={option.value}
            htmlFor={`xero-mode-${option.value}`}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
              mode === option.value ? "border-primary bg-primary/5" : "border-border hover:bg-accent/50"
            }`}
          >
            <RadioGroupItem
              id={`xero-mode-${option.value}`}
              value={option.value}
              className="mt-0.5"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-foreground">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </label>
        ))}
      </RadioGroup>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-foreground">Organisations</Label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={allSelected}
              onCheckedChange={(checked) => toggleAll(checked === true)}
              aria-label="Select all organisations"
            />
            All
          </label>
        </div>

        <ul className="divide-y divide-border rounded-md border border-border">
          {connections.map((connection) => {
            const checked = selected.has(connection.tenant_id);
            const id = `xero-org-${connection.tenant_id}`;
            return (
              <li key={connection.tenant_id} className="flex items-center gap-3 px-3 py-2">
                <Checkbox
                  id={id}
                  checked={checked}
                  onCheckedChange={(value) => toggleOne(connection.tenant_id, value === true)}
                />
                <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-baseline gap-2">
                  <span className="truncate text-sm text-foreground">
                    {connection.tenant_name || "—"}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{blocked ?? ""}</span>
        <Button onClick={start} disabled={busy || blocked !== null} className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {busy
            ? queueOnly ? "Queuing…" : "Starting…"
            : `${queueOnly ? "Queue" : "Start"} ${mode === "backfill" ? "backfill" : "incremental load"}`}
        </Button>
      </div>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && !busy && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Backfill {allSelected ? "every organisation" : `${selected.size} ${selected.size === 1 ? "organisation" : "organisations"}`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This reloads each organisation's full history rather than resuming
              from its watermark. Nothing bounds it, so it can run for some time
              and uses Fabric capacity and Xero call budget while it does.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                trigger.mutate(queueOnly);
              }}
            >
              {busy ? (queueOnly ? "Queuing…" : "Starting…") : queueOnly ? "Queue backfill" : "Start backfill"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={refusal !== null}
        onOpenChange={(open) => !open && !trigger.isPending && setRefusal(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {refusal?.reason === "queue_full"
                ? "Another run is already queued"
                : "The pipeline is already running"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {refusal && <p>{describeRunning(refusal.running)}</p>}
                {refusal?.queued && (
                  <p>
                    {refusal.queued.queued_by_you ? "You have" : "Another admin has"} already queued{" "}
                    {MODE_PHRASE[refusal.queued.mode].toLowerCase()}, and only one run can wait at a
                    time.
                  </p>
                )}
                <p>
                  {refusal?.can_queue
                    ? "Queue your run and it starts automatically as soon as nothing is running, or cancel it."
                    : "Try again once the queued run has started."}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={trigger.isPending}>
              {refusal?.can_queue ? "Cancel my run" : "Close"}
            </AlertDialogCancel>
            {refusal?.can_queue && (
              <AlertDialogAction
                disabled={trigger.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  trigger.mutate(true);
                }}
              >
                {trigger.isPending ? "Queuing…" : "Queue my run"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
